import { useBooking, useCaptureCountlyEvent, useIsMobile, useProviders } from 'hooks';
import { addGoogleTagManagerEvent } from 'modules/google-tag-manager';
import { concat, repeat, slice } from 'ramda';
import { FC, useTransition } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Appointment } from 'types';

import * as Styled from './styles';

interface Props {
    appointments: Appointment[];
    date: string;
    slotsCount: number;
    maxSlots: number;
}

const AppointmentsColumn: FC<Props> = ({ appointments, date, slotsCount, maxSlots }) => {
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const { search } = useLocation();
    const { setBookingField, booking } = useBooking();
    const { data: providers } = useProviders(booking);
    const { addCountlyEvent } = useCaptureCountlyEvent();
    const startTransition = useTransition()[1];

    const padding = isMobile ? 0 : Math.max(maxSlots - appointments.length, 0);
    const appointmentsPadded = slice(0, slotsCount, concat(appointments, repeat(null, padding)));

    const handleSelectAppointment = (appointment: Appointment) => {
        const provider = providers.find(({ providerid }) => providerid === appointment.providerid);

        addCountlyEvent({
            key: 'step3.selectSlot',
            segmentation: {
                appointmentTypeId: appointment.appointmenttypeid,
                appointmentDate: appointment.date,
                appointmentId: appointment.appointmentid,
                isPrn: provider?.isprn,
                providerId: appointment.providerid,
                providerName: provider?.displayname,
                time: appointment.starttime,
            },
        });
        addGoogleTagManagerEvent('select-appointment-slot', booking);

        startTransition(() => {
            setBookingField({ appointment });
        });

        navigate({ pathname: '/patient-information', search });
    };

    return (
        <Styled.AppointmentsColumn data-appointment-date={date}>
            <Styled.AppointmentList>
                {appointmentsPadded.map((appointment, index) =>
                    appointment ? (
                        <Styled.SlotButton
                            key={appointment.appointmentid}
                            data-appointment-id={appointment.appointmentid}
                            data-appointment-starttime={appointment.starttime}
                            data-testid="button-timeslot"
                            size={isMobile ? 'medium' : 'small'}
                            variant="secondary"
                            onClick={() => handleSelectAppointment(appointment)}
                        >
                            {appointment.displayStartTime}
                        </Styled.SlotButton>
                    ) : (
                        <Styled.SlotPlaceholder key={index}>-</Styled.SlotPlaceholder>
                    )
                )}
            </Styled.AppointmentList>
        </Styled.AppointmentsColumn>
    );
};

export { AppointmentsColumn };
